import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Alert } from 'react-native';
import { Image } from 'expo-image';
import { Feather } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { spacing, typography } from '../../theme';
import { useColors, ThemeColors } from '../../hooks/useColors';
import type { Chat } from '../../types/chat';
import type { RootStackParamList } from '../../types/navigation';
import { useAuth } from '../../hooks/useAuth';
import { useChatActions } from '../../hooks/useChatActions';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { platformAlert } from '../../utils/platformAlert';

interface ChatHeaderProps {
  chat: Chat;
}

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

export function ChatHeader({ chat }: ChatHeaderProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const navigation = useNavigation<NavigationProp>();
  const { user } = useAuth();
  const { archiveChat, restoreChat } = useChatActions();
  const [menuVisible, setMenuVisible] = useState(false);
  
  // Check if this chat is archived
  const { data: isArchived, isLoading: isArchiveStatusLoading } = useQuery({
    queryKey: ['chat-archived', chat.id, user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('archived_chats')
        .select('id')
        .eq('chat_id', chat.id)
        .eq('user_id', user?.id)
        .single();
      return !!data;
    },
    staleTime: 0, // Always fetch fresh data
  });
  
  // Determine if this is a vendor chat (vendor is not the current user)
  const isVendorChat = !!chat.vendor && chat.vendor.user_id !== user?.id;
  
  // Find the other participant (not the current user)
  const otherParticipant = chat.participants.find(p => p.user_id !== user?.id);
  
  // Log participants for debugging
  console.log('====== CHAT HEADER DEBUG =====');
  console.log('Current user ID:', user?.id);
  console.log('Chat vendor:', isVendorChat ? {
    id: chat.vendor?.id,
    store_name: chat.vendor?.store_name,
    user_id: chat.vendor?.user_id
  } : 'Not a vendor chat');
  console.log('Chat participants:', chat.participants.map(p => ({
    user_id: p.user_id,
    username: p.user?.username,
    full_name: p.user?.full_name,
    is_current_user: p.user_id === user?.id
  })));
  if (otherParticipant) {
    console.log('Other participant found:', {
      id: otherParticipant.user_id,
      username: otherParticipant.user?.username,
      full_name: otherParticipant.user?.full_name
    });
  } else {
    console.log('No other participant found');
  }

  // Determine display information for the chat
  let displayName: string;
  let avatarUrl: string | undefined;
  let userId: string | null;

  // Enhanced display logic with proper fallbacks
  // Priority: Store name > Username > Full name > "User"
  if (isVendorChat && chat.vendor) {
    // For vendor chats, always prioritize the vendor store name
    displayName = chat.vendor.store_name;
    avatarUrl = chat.vendor.logo_image;
    userId = chat.vendor.user_id;
    console.log('ChatHeader - Using vendor store name:', displayName);
  } else if (otherParticipant?.user) {
    // Check if the other participant has a store - show store name if they do
    if (otherParticipant.user.vendor?.store_name) {
      displayName = otherParticipant.user.vendor.store_name;
      avatarUrl = otherParticipant.user.avatar_url;
      console.log('ChatHeader - Using other participant store name:', displayName);
    } else if (otherParticipant.user.username) {
      displayName = `@${otherParticipant.user.username}`;
      avatarUrl = otherParticipant.user.avatar_url;
      console.log('ChatHeader - Using other participant username:', displayName);
    } else if (otherParticipant.user.full_name) {
      displayName = otherParticipant.user.full_name;
      avatarUrl = otherParticipant.user.avatar_url;
      console.log('ChatHeader - Using other participant full name:', displayName);
    } else {
      displayName = "User";
      avatarUrl = otherParticipant.user.avatar_url;
      console.log('ChatHeader - Using User fallback');
    }
    userId = otherParticipant.user_id;
  } else if (otherParticipant) {
    // Fallback for when participant exists but no user data
    displayName = "User";
    avatarUrl = undefined;
    userId = otherParticipant.user_id;
    console.log('ChatHeader - Using participant fallback:', displayName);
  } else {
    // Last resort fallback
    displayName = "Unknown User";
    avatarUrl = undefined;
    userId = null;
    console.log('ChatHeader - Using unknown user fallback');
  }
  
  console.log('Final display:', {
    displayName,
    userId,
    isVendorChat
  });
  console.log('===========================');

  const handleArchive = () => {
    setMenuVisible(false);
    platformAlert(
      'Archive Chat',
      'Are you sure you want to archive this chat?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Archive',
          onPress: () => {
            archiveChat.mutate(chat.id);
            navigation.goBack();
          },
          style: 'destructive',
        },
      ]
    );
  };

  const handleRestore = () => {
    setMenuVisible(false);
    platformAlert(
      'Restore Chat',
      'Do you want to restore this chat?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Restore',
          onPress: () => {
            restoreChat.mutate(chat.id);
            navigation.goBack();
          },
        },
      ]
    );
  };

  const handleNamePress = async () => {
    console.log('Attempting to navigate to vendor for:', { 
      isVendorChat, 
      vendorId: chat.vendor?.id, 
      userId,
      displayName,
      otherParticipant: otherParticipant ? {
        user_id: otherParticipant.user_id,
        username: otherParticipant.user?.username,
        vendor: otherParticipant.user?.vendor
      } : null
    });
    
    // For vendor chats, navigate to vendor profile directly
    if (isVendorChat && chat.vendor) {
      console.log('Navigating to vendor directly:', chat.vendor.id);
      navigation.navigate('Vendor', { id: chat.vendor.id });
      return;
    }
    
    // Check if the other participant has vendor info directly in their user object
    if (otherParticipant?.user?.vendor) {
      console.log('Found vendor info directly in participant:', otherParticipant.user.vendor);
      navigation.navigate('Vendor', { id: otherParticipant.user.vendor.id });
      return;
    }
    
    // If no embedded vendor info, query for it
    if (otherParticipant) {
      console.log('Looking up vendor for user:', otherParticipant.user_id);
      try {
        // Fetch the vendor associated with the other user's ID
        const { data: vendorData, error } = await supabase
          .from('vendors')
          .select('id, store_name, user_id')
          .eq('user_id', otherParticipant.user_id);
        
        console.log('Vendor lookup results:', { vendorData, error });
        
        if (vendorData && vendorData.length > 0 && !error) {
          console.log('Found vendor for user:', vendorData[0].id, vendorData[0].store_name);
          navigation.navigate('Vendor', { id: vendorData[0].id });
        } else {
          console.log('No vendor found for user:', otherParticipant.user_id);
          Alert.alert('No Vendor Profile', 'This user does not have a vendor profile.');
        }
      } catch (err) {
        console.error('Error getting vendor for user:', err);
        Alert.alert('Error', 'Could not retrieve vendor information.');
      }
    } else {
      console.log('No other participant found to look up vendor for');
      Alert.alert('No User Found', 'Could not identify the user to look up vendor information.');
    }
  };

  return (
    <>
      <View style={styles.container}>
        <TouchableOpacity 
          onPress={handleNamePress}
          disabled={false}
          style={styles.userInfoContent}
        >
          {avatarUrl ? (
            <Image 
              source={{ uri: avatarUrl }} 
              style={styles.avatar}
              contentFit="cover"
              cachePolicy="memory-disk"
              transition={0}
            />
          ) : (
            <View style={styles.avatarPlaceholder}>
              <Feather 
                name="user" 
                size={24} 
                color="#FFFFFF" 
              />
            </View>
          )}
          <View style={styles.nameContainer}>
            <Text style={[styles.userName, (isVendorChat || otherParticipant?.user?.vendor) && styles.vendorName]} numberOfLines={2}>
              {displayName}
            </Text>
            {(isVendorChat || otherParticipant?.user?.vendor) && (
              <Text style={styles.subtitle}>Seller</Text>
            )}
          </View>
        </TouchableOpacity>

        <TouchableOpacity 
          style={styles.menuButton}
          onPress={() => setMenuVisible(!menuVisible)}
        >
          <Feather name="more-vertical" size={24} color={colors.text} />
        </TouchableOpacity>
      </View>

      {menuVisible && (
        <View style={styles.menuOverlay}>
          <TouchableOpacity 
            style={styles.menuBackground}
            onPress={() => setMenuVisible(false)}
          />
          <View style={styles.menu}>
            {isArchiveStatusLoading ? (
              <View style={styles.menuItem}>
                <Text style={[styles.menuText, { color: colors.textLight }]}>Loading...</Text>
              </View>
            ) : isArchived ? (
              <TouchableOpacity 
                style={styles.menuItem}
                onPress={handleRestore}
              >
                <Feather name="inbox" size={20} color={colors.primary} />
                <Text style={[styles.menuText, { color: colors.primary }]}>Restore Chat</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity 
                style={styles.menuItem}
                onPress={handleArchive}
              >
                <Feather name="archive" size={20} color={colors.text} />
                <Text style={styles.menuText}>Archive Chat</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      )}
    </>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background,
    zIndex: 1,
  },
  userInfoContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 14,
    marginRight: spacing.sm,
    backgroundColor: colors.surface,
  },
  avatarPlaceholder: {
    width: 44,
    height: 44,
    borderRadius: 14,
    backgroundColor: colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  placeholderImage: {
    width: '100%',
    height: '100%',
    borderRadius: 14,
  },
  nameContainer: {
    flex: 1,
  },
  userName: {
    fontSize: 17,
    fontWeight: '700',
    color: colors.text,
    letterSpacing: -0.3,
  },
  vendorName: {
    color: colors.primary,
  },
  subtitle: {
    fontSize: 12,
    color: colors.textLight,
    fontWeight: '500',
    marginTop: 1,
  },
  menuButton: {
    width: 44,
    height: 44,
    borderRadius: 14,
    backgroundColor: colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.border,
  },
  menuOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
  },
  menuBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.1)',
  },
  menu: {
    position: 'absolute',
    top: 70,
    right: spacing.md,
    backgroundColor: colors.background,
    borderRadius: 16,
    padding: spacing.xs,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
    borderWidth: 1,
    borderColor: colors.border,
    minWidth: 180,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    gap: spacing.sm,
    borderRadius: 12,
  },
  menuText: {
    fontSize: 15,
    fontWeight: '500',
    color: colors.text,
  },
}); 